'use client';

import { useCallback, useEffect, useState } from 'react';
import { STORAGE_KEY as GRAPHICS_STORAGE_KEY } from '../settings/graphics-settings';

/**
 * localStorage gates for the lobby onboarding flows.
 *
 * Keys are version-suffixed so we can re-show the flow to existing users
 * after meaningful copy or scene changes — bump the suffix in a single
 * place when that happens.
 *
 * Pattern follows `src/components/modals/grid-onboarding-modal.tsx`:
 * read inside an effect (avoid SSR access), one boolean flag per flow,
 * a tiny `?onboarding=reset` URL bypass for QA.
 */

export const PLAYER_KEY = 'takeover-player-onboarded-v1';
/** Legacy prefix for the per-address player-tour flag. The tour is now
 *  keyed browser-wide (see `usePlayerOnboarding`), but we still want a
 *  `?onboarding=reset` to mop up any residual per-address keys from the
 *  old behavior so returning testers get a clean slate. Kept as an
 *  internal constant; not exported. */
const PLAYER_ADDRESS_KEY_PREFIX = `${PLAYER_KEY}:`;
export const CREATOR_KEY = 'takeover-creator-onboarded-v1';
/** One-shot handoff written by LaunchClient.onLaunch and consumed on the lobby. */
export const RECAP_PENDING_KEY = 'takeover-creator-launched-pending';
/** Sticky "this user has launched at least once" — distinct from the one-shot handoff. */
export const RECAP_DONE_KEY = 'takeover-creator-launched-v1';
/** Mirrors `DONE_KEY_PREFIX` in `use-welcome-tail.ts`. Duplicated here so the
 *  `?onboarding=reset` sweep can clear every per-address tail flag without
 *  importing from a sibling module (which would create a cycle). */
const WELCOME_TAIL_ADDRESS_KEY_PREFIX = 'takeover-welcome-tail-done-v1:';

const RESET_PARAM = 'onboarding';
const RESET_VALUE = 'reset';

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function safeRead(key: string): string | null {
  if (!isBrowser()) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: string) {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore quota / privacy-mode errors
  }
}

function safeRemove(key: string) {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/** True the first time this hook mounts after `?onboarding=reset` is in the URL. */
function consumeResetParam(): boolean {
  if (!isBrowser()) return false;
  const url = new URL(window.location.href);
  if (url.searchParams.get(RESET_PARAM) !== RESET_VALUE) return false;
  // Strip the param so subsequent navigations don't keep firing.
  url.searchParams.delete(RESET_PARAM);
  window.history.replaceState({}, '', url.toString());
  safeRemove(PLAYER_KEY);
  safeRemove(CREATOR_KEY);
  safeRemove(RECAP_PENDING_KEY);
  safeRemove(RECAP_DONE_KEY);
  // Sweep address-scoped flags too. The set of addresses in play is open-ended
  // (every wallet a tester has ever connected in this browser), so enumerate
  // `localStorage` and nuke any key under the two known prefixes. Without this
  // sweep `?onboarding=reset` would leave the post-tour WelcomeTail marked
  // done for the currently-connected wallet.
  try {
    const stripped: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (!k) continue;
      if (
        k.startsWith(PLAYER_ADDRESS_KEY_PREFIX) ||
        k.startsWith(WELCOME_TAIL_ADDRESS_KEY_PREFIX)
      ) {
        stripped.push(k);
      }
    }
    for (const k of stripped) safeRemove(k);
  } catch {
    // ignore — enumeration failures are best-effort
  }
  return true;
}

interface FlagControl {
  open: boolean;
  dismiss: (options?: { remember?: boolean }) => void;
  reset: () => void;
}

/** Generic single-flag flow controller. Used by the creator tour (which
 *  is keyed per-browser because the creator wizard is always the same
 *  session). The player tour uses its own address-aware variant below.
 *
 *  Lazy-initialises from `localStorage` so the first render already
 *  knows whether the tour is open. Without this the lobby renders
 *  one frame with `open === false`, mounts `BoardCanvas3D` (which
 *  starts compiling shaders + baking PMREM env maps), and only then
 *  the effect flips `open` to `true`. On weak GPUs that single wasted
 *  init was enough to stall Chrome's GPU process for several seconds
 *  immediately after `AccessGate` unlocked. The provider above this
 *  hook (`AccessGate`) renders a black bed during the SSR/hydration
 *  pass so reading `localStorage` here is safe. */
function useFlagFlow(key: string): FlagControl {
  const [open, setOpen] = useState<boolean>(() => {
    if (!isBrowser()) return false;
    return safeRead(key) !== '1';
  });

  useEffect(() => {
    // The reset param sweep still runs in an effect because it
    // mutates the URL via `history.replaceState` — which we don't
    // want to do during render. If a reset is in the URL we may
    // need to re-open here for users who already had the flag set.
    if (consumeResetParam()) {
      setOpen(true);
      return;
    }
    if (safeRead(key) === '1') {
      setOpen(false);
      return;
    }
    setOpen(true);
  }, [key]);

  const dismiss = useCallback(
    ({ remember = true }: { remember?: boolean } = {}) => {
      setOpen(false);
      if (remember) safeWrite(key, '1');
    },
    [key],
  );

  const reset = useCallback(() => {
    safeRemove(key);
    setOpen(true);
  }, [key]);

  return { open, dismiss, reset };
}

/**
 * Browser-wide player tour controller.
 *
 * The tour is a first-visit explainer, deliberately decoupled from any
 * wallet: every new browser sees it exactly once, regardless of which
 * address eventually connects (or if none does at all). A previous
 * iteration keyed the flag per-address, but the tour is really
 * "introduce me to the product" — a second wallet on the same browser
 * already knows what the product is, so re-firing it was noise.
 *
 * The post-connect plinth picker (`useWelcomeTail`) remains per-address
 * because that one *is* about the wallet (picking a personal plinth).
 *
 * Gating key: `PLAYER_KEY` in `localStorage`. Bypass with
 * `?onboarding=reset` (handled globally by `consumeResetParam`).
 */
export function usePlayerOnboarding(): FlagControl {
  return useFlagFlow(PLAYER_KEY);
}

export function useCreatorOnboarding(): FlagControl {
  return useFlagFlow(CREATOR_KEY);
}

interface RecapControl {
  /** True on lobby mount when LaunchClient just navigated us back. */
  pending: boolean;
  /** Mark recap consumed (also flips the sticky "launched once" flag). */
  clear: () => void;
  /** Called from LaunchClient.onLaunch right before navigation. */
  markLaunched: () => void;
  /** QA replay — re-fires the recap on next lobby mount. */
  reset: () => void;
}

export function useLaunchRecap(): RecapControl {
  const [pending, setPending] = useState(false);

  useEffect(() => {
    consumeResetParam();
    if (safeRead(RECAP_PENDING_KEY) === '1') {
      setPending(true);
    }
  }, []);

  const clear = useCallback(() => {
    setPending(false);
    safeRemove(RECAP_PENDING_KEY);
    safeWrite(RECAP_DONE_KEY, '1');
  }, []);

  const markLaunched = useCallback(() => {
    safeWrite(RECAP_PENDING_KEY, '1');
  }, []);

  const reset = useCallback(() => {
    safeWrite(RECAP_PENDING_KEY, '1');
    setPending(true);
  }, []);

  return { pending, clear, markLaunched, reset };
}

/**
 * Lite/mobile fallback gate for the onboarding stage.
 *
 * Stays decoupled from `useGraphics()` so the wizard route (which doesn't
 * mount `GraphicsProvider`) can still call it. Reads the same localStorage
 * key the GraphicsContext writes, plus a viewport check.
 */
export function useTwoDFallback(): boolean {
  const [twoD, setTwoD] = useState(false);

  useEffect(() => {
    if (!isBrowser()) return;

    const update = () => {
      const stored = safeRead(GRAPHICS_STORAGE_KEY);
      // 'low' is the legacy id for what's now 'lite' — see graphics-settings.ts.
      const isLite = stored === 'lite' || stored === 'low';
      const isNarrow = window.matchMedia('(max-width: 767px)').matches;
      setTwoD(isLite || isNarrow);
    };

    update();

    const mq = window.matchMedia('(max-width: 767px)');
    const onMq = () => update();
    const onStorage = (event: StorageEvent) => {
      if (event.key === GRAPHICS_STORAGE_KEY) update();
    };

    mq.addEventListener('change', onMq);
    window.addEventListener('storage', onStorage);
    return () => {
      mq.removeEventListener('change', onMq);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  return twoD;
}
